import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, Company, Sector } from "@/types";

interface SectorNewProps {
  sector: Sector;
  companies: Company[];
}

export default function SectorNew({ sector, companies }: SectorNewProps) {
  const routes = useOrganizationRoutes();
  const { data, setData, post, processing, errors, transform } = useForm({
    name: sector.name ?? "",
    code: sector.code ?? "",
    description: sector.description ?? "",
    client: sector.client ?? "",
    project_manager: sector.project_manager ?? "",
    address: sector.address ?? "",
    active: sector.active ?? true,
    company_id: sector.company_id ? String(sector.company_id) : "",
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Sectores",
      href: routes.sectors.index(),
    },
    {
      title: "Nuevo Sector",
      href: "#",
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transform((formData) => ({
      sector: {
        ...formData,
        company_id: formData.company_id ? Number(formData.company_id) : null,
      },
    }));

    post(routes.sectors.index());
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Nuevo Sector" />
      <div className="flex h-full flex-1 flex-col gap-6 p-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={routes.sectors.index()}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Nuevo Sector</h1>
            <p className="text-muted-foreground">
              Crea un nuevo sector u obra en el sistema
            </p>
          </div>
        </div>

        {/* Form */}
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Información del Sector</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    placeholder="Nombre del sector"
                    disabled={processing}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="code">Código *</Label>
                  <Input
                    id="code"
                    value={data.code}
                    onChange={(e) => setData("code", e.target.value)}
                    placeholder="Código único del sector"
                    disabled={processing}
                  />
                  {errors.code && (
                    <p className="text-sm text-red-500">{errors.code}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="company_id">Compañía</Label>
                  <Select
                    value={data.company_id || undefined}
                    onValueChange={(value) => setData("company_id", value)}
                    disabled={processing}
                  >
                    <SelectTrigger id="company_id">
                      <SelectValue placeholder="Compañía por asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={String(company.id)}>
                          {company.name ?? company.legal_name ?? `Compañía ${company.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.company_id && (
                    <p className="text-sm text-red-500">{errors.company_id}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="client">Cliente</Label>
                <Input
                  id="client"
                  value={data.client}
                  onChange={(e) => setData("client", e.target.value)}
                  placeholder="Nombre del cliente"
                  disabled={processing}
                />
                {errors.client && (
                  <p className="text-sm text-red-500">{errors.client}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="project_manager">Jefe de Proyecto</Label>
                <Input
                  id="project_manager"
                  value={data.project_manager}
                  onChange={(e) => setData("project_manager", e.target.value)}
                  placeholder="Nombre del jefe de proyecto"
                  disabled={processing}
                />
                {errors.project_manager && (
                  <p className="text-sm text-red-500">
                    {errors.project_manager}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={data.address}
                  onChange={(e) => setData("address", e.target.value)}
                  placeholder="Dirección de la obra"
                  disabled={processing}
                />
                {errors.address && (
                  <p className="text-sm text-red-500">{errors.address}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción (opcional)</Label>
                <TextareaWithCounter
                  id="description"
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                  placeholder="Descripción del sector"
                  disabled={processing}
                  rows={3}
                  maxLength={500}
                  showCharacterCount={true}
                />
                {errors.description && (
                  <p className="text-sm text-red-500">{errors.description}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="active"
                  checked={data.active}
                  onCheckedChange={(checked) =>
                    setData("active", checked === true)
                  }
                  disabled={processing}
                />
                <Label htmlFor="active" className="cursor-pointer">
                  Sector activo
                </Label>
              </div>

              <div className="flex items-center gap-4">
                <Button type="submit" disabled={processing}>
                  <Save className="h-4 w-4 mr-2" />
                  {processing ? "Creando..." : "Crear Sector"}
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link href={routes.sectors.index()}>Cancelar</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
